import { Slot } from 'radix-ui';
import React, { type ComponentPropsWithRef, type ReactNode } from 'react';
import { cx } from '@mildang/styled-system/css';
import { Spinner } from '../Spinner';
import type { SpinnerColor } from '../Spinner/type';

// Spinner 의 색 union 을 단일 소스(Spinner/type)에서 재사용한다.
// ButtonBase 내부 소비자(Button/IconButton 등)가 subset(white/neutral 등)만 넘겨도 타입상 문제 없고,
// 향후 brand/inverse 를 넘겨야 할 때 별도 확장 없이 자동 지원된다.
export type { SpinnerColor };

export interface ButtonBaseClasses {
  root: string;
  icon: string;
  startIcon: string;
  endIcon: string;
  spinner: string;
}

export interface ButtonBaseProps
  extends Omit<ComponentPropsWithRef<'button'>, 'ref' | 'className'> {
  asChild?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  loading?: boolean;
  loadingColor?: SpinnerColor;
  loadingSize?: number;
  classes: ButtonBaseClasses;
  /**
   * 호출자가 splitCssProps로 panda style props를 처리한 결과의 className을 전달한다.
   * @remarks asChild=true 사용 시 ref는 자식 element를 가리킨다.
   * ref 타입은 HTMLButtonElement로 단언되지만 실제 element가 다를 수 있으므로
   * button 전용 API(form 매칭 등) 사용은 피한다.
   */
  className?: string;
}

export const ButtonBase = React.forwardRef<HTMLButtonElement, ButtonBaseProps>(
  (props, forwardedRef) => {
    const {
      asChild = false,
      startIcon,
      endIcon,
      loading = false,
      loadingColor = 'neutral',
      loadingSize,
      classes,
      className,
      children,
      type = 'button',
      disabled = false,
      ...rest
    } = props;

    const Comp = asChild ? Slot.Root : 'button';

    return (
      <Comp
        ref={forwardedRef}
        type={asChild ? undefined : type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        data-loading={loading ? 'true' : undefined}
        data-has-start-icon={startIcon ? 'true' : undefined}
        data-has-end-icon={endIcon ? 'true' : undefined}
        {...rest}
        className={cx(classes.root, className)}
      >
        {startIcon && !loading && (
          <span className={cx(classes.icon, classes.startIcon)}>{startIcon}</span>
        )}
        {loading && <Spinner className={classes.spinner} color={loadingColor} size={loadingSize} />}
        <Slot.Slottable>{children}</Slot.Slottable>
        {endIcon && !loading && (
          <span className={cx(classes.icon, classes.endIcon)}>{endIcon}</span>
        )}
      </Comp>
    );
  },
);

ButtonBase.displayName = 'ButtonBase';
